<template>
  <section class="mx-auto flex min-h-[70vh] max-w-4xl flex-col gap-8 px-6 py-16">
    <div class="flex flex-col gap-2">
      <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('notificationsPage.eyebrow') }}</p>
      <h1 class="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
        {{ $t('notificationsPage.title') }}
      </h1>
      <p class="text-base text-steel-200/90">
        {{ $t('notificationsPage.description') }}
      </p>
    </div>

    <div class="rounded-2xl border border-white/10 bg-midnight-800/60 p-5 shadow-glow">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('notificationsPage.pushEyebrow') }}</p>
          <div class="mt-2 flex items-center gap-3">
            <p class="text-lg font-semibold text-white">{{ $t('notificationsPage.pushTitle') }}</p>
            <span class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-steel-300">{{ pushStatusLabel }}</span>
          </div>
          <p class="mt-2 text-sm text-steel-300">{{ pushDescription }}</p>
          <p v-if="pushError" class="mt-2 text-sm text-rose-200">{{ pushError }}</p>
        </div>
        <button
          v-if="!pushEnabled"
          type="button"
          class="rounded-2xl border border-white/10 bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="pushBusy || !pushSupported"
          @click="enablePushNotifications"
        >
          {{ pushBusy ? $t('notificationsPage.enabling') : $t('notificationsPage.enablePush') }}
        </button>
        <button
          v-else
          type="button"
          class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="pushBusy"
          @click="disablePushNotifications"
        >
          {{ pushBusy ? $t('notificationsPage.updating') : $t('notificationsPage.disableDevice') }}
        </button>
      </div>
    </div>

    <div v-if="!notificationsLoading && activityHighlights.length" class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <div
        v-for="item in activityHighlights"
        :key="item.key"
        class="rounded-2xl border border-white/10 bg-midnight-800/60 p-4 shadow-glow"
      >
        <div class="flex items-center justify-between gap-3">
          <span class="inline-flex h-10 w-10 items-center justify-center rounded-2xl border text-base" :class="item.iconClass">
            {{ item.icon }}
          </span>
          <p class="text-2xl font-semibold text-white">{{ item.count }}</p>
        </div>
        <p class="mt-3 text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ item.label }}</p>
        <p class="mt-1 text-sm text-steel-300">{{ item.caption }}</p>
      </div>
    </div>

    <div v-if="notificationsLoading" class="space-y-4">
      <div
        v-for="item in 5"
        :key="`notification-skeleton-${item}`"
        class="rounded-2xl border border-white/10 bg-midnight-800/60 p-5 animate-pulse"
      >
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div class="h-10 w-10 rounded-2xl bg-white/10"></div>
          <div class="min-w-0 flex-1 space-y-2">
            <div class="h-3 w-5/6 rounded bg-white/10"></div>
            <div class="h-2 w-24 rounded bg-white/10"></div>
          </div>
          <div class="h-3 w-12 rounded bg-white/10"></div>
        </div>
      </div>
    </div>

    <div v-else class="space-y-4">
      <div
        v-for="note in notifications"
        :key="note.id"
        class="rounded-2xl border border-white/10 bg-midnight-800/60 p-5"
      >
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div class="flex items-center gap-3">
            <router-link
              v-if="!note.isBroadcast"
              :to="`/u/${note.actorHandle}`"
              class="flex items-center gap-3 transition hover:opacity-80"
            >
              <div class="h-10 w-10 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
                <img
                  v-if="note.actorAvatar"
                  :src="note.actorAvatar"
                  alt="Actor avatar"
                  class="h-full w-full object-cover"
                  referrerpolicy="no-referrer"
                  crossorigin="anonymous"
                />
                <div v-else class="flex h-full w-full items-center justify-center text-xs font-semibold text-steel-200">
                  {{ note.actorInitials }}
                </div>
              </div>
            </router-link>
            <div
              v-else
              class="flex h-10 w-10 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70 text-xs font-semibold uppercase tracking-[0.25em] text-steel-200"
            >
              DR
            </div>
            <span class="inline-flex h-10 w-10 items-center justify-center rounded-2xl border text-base" :class="note.iconClass">
              {{ note.icon }}
            </span>
          </div>
          <div class="min-w-0 flex-1">
            <p v-if="note.isBroadcast" class="text-sm text-steel-200/90">
              <span class="font-semibold text-white">{{ note.title }}</span>
              <span> {{ note.message }}</span>
            </p>
            <p v-else class="text-sm text-steel-200/90">
              <span class="inline-flex items-center gap-1.5">
                <router-link :to="`/u/${note.actorHandle}`" class="font-semibold text-white hover:text-neon-400">
                  {{ note.actorName }}
                </router-link>
                <verified-badge v-if="note.actorVerified" size="xs" />
              </span>
              <router-link :to="`/u/${note.actorHandle}`" class="text-steel-400 hover:text-neon-400">
                (@{{ note.actorHandle }})
              </router-link>
              <span> {{ note.message }}</span>
            </p>
            <div class="mt-2 flex flex-wrap items-center gap-2">
              <span class="rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.25em]" :class="note.pillClass">
                {{ note.pillLabel }}
              </span>
              <p class="text-xs text-steel-400">{{ note.createdLabel }}</p>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-4 sm:ml-auto">
            <router-link
              v-if="note.post_id"
              class="text-xs uppercase tracking-[0.3em] text-neon-400 hover:text-neon-500"
              :to="`/post/${note.post_id}`"
            >
              {{ $t('notificationsPage.view') }}
            </router-link>
            <router-link
              v-else-if="note.isBroadcast && note.url"
              class="text-xs uppercase tracking-[0.3em] text-neon-400 hover:text-neon-500"
              :to="note.url"
            >
              {{ $t('notificationsPage.view') }}
            </router-link>
            <button
              v-if="!note.isBroadcast"
              type="button"
              class="text-xs uppercase tracking-[0.3em] text-steel-400 hover:text-white"
              @click="deleteNotification(note.id)"
            >
              {{ $t('notificationsPage.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
      {{ errorMessage }}
    </p>
  </section>
</template>

<script>
import { supabase, getAvatarUrl, refreshReadySession, runSupabaseQueryWithRetry } from '@/lib/supabase'
import { emitAppToast } from '@/lib/toast'
import { getPushPermissionState, getPushUnsupportedReason, hasPushSubscription, isPushSupported, requestPushPermissionAndSubscribe, unsubscribePushNotifications } from '@/lib/push'
import VerifiedBadge from '@/components/VerifiedBadge.vue'

export default {
  name: 'NotificationsView',
  components: {
    VerifiedBadge,
  },
  data() {
    return {
      notifications: [],
      errorMessage: '',
      userId: '',
      notificationsLoading: true,
      pushSupported: false,
      pushPermission: 'default',
      pushEnabled: false,
      pushBusy: false,
      pushError: '',
      visibilityHandler: null,
      isRefreshingView: false,
    }
  },
  computed: {
    pushStatusLabel() {
      if (!this.pushSupported) return this.$t('notificationsPage.statusUnsupported')
      if (this.pushEnabled) return this.$t('notificationsPage.statusEnabled')
      if (this.pushPermission === 'granted') return this.$t('notificationsPage.statusOff')
      if (this.pushPermission === 'denied') return this.$t('notificationsPage.statusBlocked')
      return this.$t('notificationsPage.statusOff')
    },
    pushDescription() {
      if (!this.pushSupported) return getPushUnsupportedReason()
      if (this.pushEnabled) return this.$t('notificationsPage.descEnabled')
      if (this.pushPermission === 'granted') return this.$t('notificationsPage.descGranted')
      if (this.pushPermission === 'denied') return this.$t('notificationsPage.descDenied')
      return this.$t('notificationsPage.descDefault')
    },
    activityHighlights() {
      const since = Date.now() - (24 * 60 * 60 * 1000)
      const counts = {
        likes: 0,
        saves: 0,
        comments: 0,
        follows: 0,
      }

      this.notifications.forEach((note) => {
        const createdAt = new Date(note.created_at).getTime()
        if (!Number.isFinite(createdAt) || createdAt < since) return

        if (note.type === 'post_like' || String(note.type || '').startsWith('post_reaction_') || note.type === 'comment_like') {
          counts.likes += 1
          return
        }

        if (note.type === 'post_save') {
          counts.saves += 1
          return
        }

        if (note.type === 'post_comment' || note.type === 'comment_reply') {
          counts.comments += 1
          return
        }

        if (note.type === 'follow') {
          counts.follows += 1
        }
      })

      return [
        { key: 'likes', label: 'Last 24H', count: counts.likes, caption: 'likes and reactions landed', icon: '❤', iconClass: 'border-rose-400/30 bg-rose-400/10 text-rose-200' },
        { key: 'saves', label: 'Saved Builds', count: counts.saves, caption: 'people saved your posts', icon: '✦', iconClass: 'border-amber-400/30 bg-amber-400/10 text-amber-200' },
        { key: 'comments', label: 'Conversations', count: counts.comments, caption: 'comments and replies came in', icon: '💬', iconClass: 'border-sky-400/30 bg-sky-400/10 text-sky-200' },
        { key: 'follows', label: 'New Followers', count: counts.follows, caption: 'fresh people joined your circle', icon: '↗', iconClass: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200' },
      ].filter((item) => item.count > 0)
    },
  },
  async mounted() {
    await this.refreshViewData()
    await this.markNotificationsAsRead()
    await this.loadPushState()

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.refreshViewData({ silent: true })
        this.loadPushState()
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)
  },
  beforeUnmount() {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
  },
  methods: {
    async markNotificationsAsRead() {
      if (!this.userId) return

      try {
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('recipient_id', this.userId)
          .eq('is_read', false)
      } catch (_error) {
        // Best-effort; the page should still render even if the update fails.
      }
    },
    async loadPushState() {
      this.pushSupported = isPushSupported()
      this.pushPermission = getPushPermissionState()
      this.pushEnabled = this.pushPermission === 'granted' && await hasPushSubscription()
    },
    async enablePushNotifications() {
      this.pushError = ''
      this.pushBusy = true

      try {
        const session = await refreshReadySession()
        await requestPushPermissionAndSubscribe(session)
        await this.loadPushState()
        emitAppToast({
          title: 'Push enabled',
          message: 'You will now receive DRVN system notifications on this device.',
          tone: 'success',
        })
      } catch (error) {
        this.pushError = error.message || 'Unable to enable notifications on this device.'
      } finally {
        this.pushBusy = false
      }
    },
    async disablePushNotifications() {
      this.pushError = ''
      this.pushBusy = true

      try {
        const session = await refreshReadySession()
        await unsubscribePushNotifications(session)
        emitAppToast({
          title: 'Push disabled',
          message: 'System notifications were removed from this device.',
          tone: 'info',
        })
      } catch (error) {
        this.pushError = error.message || 'Unable to disable notifications on this device.'
      } finally {
        await this.loadPushState()
        this.pushBusy = false
      }
    },
    formatRelativeTime(value) {
      const timestamp = new Date(value).getTime()
      if (!Number.isFinite(timestamp)) return new Date(value).toLocaleString()

      const diffMs = Date.now() - timestamp
      const diffMinutes = Math.max(0, Math.floor(diffMs / 60000))
      if (diffMinutes < 1) return 'just now'
      if (diffMinutes < 60) return `${diffMinutes}m ago`

      const diffHours = Math.floor(diffMinutes / 60)
      if (diffHours < 24) return `${diffHours}h ago`

      const diffDays = Math.floor(diffHours / 24)
      if (diffDays < 7) return `${diffDays}d ago`

      return new Date(value).toLocaleString()
    },
    getNotificationUi(type) {
      if (String(type || '').startsWith('post_reaction_')) {
        const reactionTone = String(type || '').replace('post_reaction_', '').replaceAll('_', ' ')
        return {
          message: `reacted ${reactionTone} to your post.`,
          icon: '⚡',
          iconClass: 'border-violet-400/30 bg-violet-400/10 text-violet-200',
          pillLabel: 'Reaction',
          pillClass: 'border-violet-400/30 bg-violet-400/10 text-violet-100',
        }
      }

      const uiMap = {
        post_like: {
          message: 'liked your post.',
          icon: '❤',
          iconClass: 'border-rose-400/30 bg-rose-400/10 text-rose-200',
          pillLabel: 'Like',
          pillClass: 'border-rose-400/30 bg-rose-400/10 text-rose-100',
        },
        post_save: {
          message: 'saved your post for later.',
          icon: '✦',
          iconClass: 'border-amber-400/30 bg-amber-400/10 text-amber-200',
          pillLabel: 'Saved',
          pillClass: 'border-amber-400/30 bg-amber-400/10 text-amber-100',
        },
        post_comment: {
          message: 'commented on your post.',
          icon: '💬',
          iconClass: 'border-sky-400/30 bg-sky-400/10 text-sky-200',
          pillLabel: 'Comment',
          pillClass: 'border-sky-400/30 bg-sky-400/10 text-sky-100',
        },
        comment_reply: {
          message: 'replied to your comment.',
          icon: '↩',
          iconClass: 'border-cyan-400/30 bg-cyan-400/10 text-cyan-200',
          pillLabel: 'Reply',
          pillClass: 'border-cyan-400/30 bg-cyan-400/10 text-cyan-100',
        },
        comment_like: {
          message: 'liked your comment.',
          icon: '❤',
          iconClass: 'border-pink-400/30 bg-pink-400/10 text-pink-200',
          pillLabel: 'Comment Like',
          pillClass: 'border-pink-400/30 bg-pink-400/10 text-pink-100',
        },
        follow: {
          message: 'started following you.',
          icon: '↗',
          iconClass: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200',
          pillLabel: 'Follow',
          pillClass: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-100',
        },
        broadcast: {
          message: '',
          icon: '📣',
          iconClass: 'border-amber-300/30 bg-amber-300/10 text-amber-100',
          pillLabel: 'Broadcast',
          pillClass: 'border-amber-300/30 bg-amber-300/10 text-amber-100',
        },
      }

      return uiMap[type] || {
        message: 'interacted with you.',
        icon: '•',
        iconClass: 'border-white/10 bg-white/5 text-steel-200',
        pillLabel: 'Activity',
        pillClass: 'border-white/10 bg-white/5 text-steel-200',
      }
    },
    async refreshViewData(options = {}) {
      if (this.isRefreshingView) return

      const { silent = false } = options
      this.isRefreshingView = true

      try {
        if (!silent) {
          this.notificationsLoading = true
        }
        const session = await refreshReadySession()
        this.userId = session?.user?.id || ''
        await this.fetchNotifications()
      } finally {
        this.isRefreshingView = false
      }
    },
    async fetchNotifications() {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to view notifications.'
        this.notificationsLoading = false
        return
      }

      const [personalResult, broadcastResult] = await Promise.all([
        runSupabaseQueryWithRetry(() => (
          supabase
            .from('notifications')
            .select(
              'id, type, created_at, post_id, comment_id, actor_id, actor:profiles!notifications_actor_id_fkey (handle, full_name, avatar_url, avatar_path, verified)'
            )
            .eq('recipient_id', this.userId)
            .order('created_at', { ascending: false })
        )),
        runSupabaseQueryWithRetry(() => (
          supabase
            .from('broadcast_notifications')
            .select('id, title, body, url, created_at, expires_at')
            .gt('expires_at', new Date().toISOString())
            .order('created_at', { ascending: false })
        )),
      ])

      if (personalResult.error || broadcastResult.error) {
        this.errorMessage = 'Unable to load notifications.'
        this.notificationsLoading = false
        return
      }

      const personalNotifications = (personalResult.data || []).map((note) => {
        const profile = note.actor || {}
        const actorAvatar = getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url })
        const ui = this.getNotificationUi(note.type)

        return {
          ...note,
          actorName: profile.full_name || 'DRVN Member',
          actorHandle: profile.handle || 'member',
          actorVerified: Boolean(profile.verified),
          actorAvatar,
          actorInitials: (profile.full_name || 'D')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((chunk) => chunk[0].toUpperCase())
            .join(''),
          message: ui.message,
          icon: ui.icon,
          iconClass: ui.iconClass,
          pillLabel: ui.pillLabel,
          pillClass: ui.pillClass,
          createdLabel: this.formatRelativeTime(note.created_at),
        }
      })

      const broadcastNotifications = (broadcastResult.data || []).map((note) => {
        const ui = this.getNotificationUi('broadcast')

        return {
          ...note,
          id: `broadcast-${note.id}`,
          isBroadcast: true,
          message: note.body,
          icon: ui.icon,
          iconClass: ui.iconClass,
          pillLabel: ui.pillLabel,
          pillClass: ui.pillClass,
          createdLabel: this.formatRelativeTime(note.created_at),
        }
      })

      this.notifications = [...personalNotifications, ...broadcastNotifications]
        .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime())
      this.notificationsLoading = false
    },
    async deleteNotification(noteId) {
      const { error } = await supabase.from('notifications').delete().eq('id', noteId)
      if (error) {
        this.errorMessage = error.message
        return
      }
      this.notifications = this.notifications.filter((note) => note.id !== noteId)
    },
  },
}
</script>
